import { useTranslation } from 'react-i18next';

import type { FormInstance } from '@arco-design/web-react';
import { Form, Input, InputNumber } from '@arco-design/web-react';

import { CapacitySlider, CapacitySliderRow, LARGE_RANGE_SLIDER_MARK_COUNT } from '@/components/CapacitySlider';

import { DEFAULT_QUANTITY_UNITS } from '../quantityUnit';
import styles from './index.module.less';

const FormItem = Form.Item;

/** CPU 表单路径（弹窗/主表单共用形状） */
export interface CpuConfigFieldPaths {
  cpuCards: string;
  cpuCores: string;
  cpuLogical: string;
}

interface CpuConfigFieldsProps {
  paths: CpuConfigFieldPaths;
  cpuMax: number;
  cpuErr: string;
  checkCpuLimit: (value: number | undefined, cb: (error?: string) => void) => void;
  cpuTotal: number;
  /** 直建默认「核」；计价时传入选型 quantityUnit */
  coreLabel: string;
}

interface ContainerSimpleCpuFieldsProps {
  form: FormInstance;
  cpuRequestPath: string;
  cpuMin?: number;
  cpuMax: number;
  readonly?: boolean;
  disabled?: boolean;
  /** 数量单位展示，默认 Core 对应 i18n 核 */
  quantityUnitLabel?: string;
}

/** 容器直建：单字段 CPU 核心数 */
export function ContainerSimpleCpuFields({
  form,
  cpuRequestPath,
  cpuMin = 1,
  cpuMax,
  readonly,
  disabled,
  quantityUnitLabel,
}: ContainerSimpleCpuFieldsProps) {
  const { t } = useTranslation('cluster');
  const cpuWatch = Form.useWatch(cpuRequestPath, form);
  const unitLabel = quantityUnitLabel || t('core-pl');

  const checkCpuLimit = (value: number | undefined, cb: (error?: string) => void) => {
    if (value == null) {
      cb(t('must-select'));
      return;
    }
    const cores = Number(value);
    if (!Number.isFinite(cores) || cores < cpuMin) {
      cb(`CPU 最小值为 ${cpuMin} ${unitLabel}`);
      return;
    }
    if (cpuMax > 0 && cores > cpuMax) {
      cb(`${t('limitlarge')}${cpuMax}`);
      return;
    }
    cb();
  };

  const cpu = Number(cpuWatch);

  return (
    <div className={styles.cpuSection}>
      <FormItem
        label={t('cpu-core-number')}
        field={cpuRequestPath}
        rules={readonly || disabled ? undefined : [{ required: true, validator: checkCpuLimit }]}
        disabled={disabled}
        initialValue={1}
      >
        <InputNumber
          min={cpuMin}
          max={cpuMax || 128}
          mode="button"
          placeholder={t('place', { name: t('cpu-core-number') })}
          disabled={readonly || disabled}
        />
      </FormItem>
      <div className={styles.cpuTotalRow}>
        cpu: {cpu || 0} {unitLabel}
        {cpuMax ? (
          <span className={styles.cpuHint}>
            （{t('limitlarge')}
            {cpuMax}）
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** 云主机 CPU：物理卡 / 物理核 / 逻辑核 */
export function CpuConfigFields({ paths, cpuMax, cpuErr, checkCpuLimit, cpuTotal, coreLabel }: CpuConfigFieldsProps) {
  const { t } = useTranslation('cluster');

  return (
    <div className={styles.cpuSection}>
      <div className={styles.cpuFieldsRow}>
        <FormItem initialValue={1} label={t('cpu-core-num')} field={paths.cpuCards} rules={[{ required: true, validator: checkCpuLimit }]}>
          <InputNumber min={1} max={128} mode="button" placeholder={t('place', { name: t('cpu-core-num') })} />
        </FormItem>
        <FormItem initialValue={1} label={t('cpu-core-core')} field={paths.cpuCores} rules={[{ required: true, validator: checkCpuLimit }]}>
          <InputNumber min={1} max={128} mode="button" placeholder={t('place', { name: t('cpu-core-core') })} />
        </FormItem>
        <FormItem
          initialValue={1}
          label={t('cpu-logical-core')}
          field={paths.cpuLogical}
          rules={[{ required: true, validator: checkCpuLimit }]}
        >
          <InputNumber min={1} max={2} mode="button" placeholder={t('place', { name: t('cpu-logical-core') })} />
        </FormItem>
      </div>
      <div className={styles.cpuTotalRow}>
        cpu: {cpuTotal} {coreLabel}
        {cpuErr ? <span className={styles.cpuSummary}>{cpuErr}</span> : null}
        {cpuMax ? (
          <span className={styles.cpuHint}>
            （{t('limitlarge')}
            {cpuMax}）
          </span>
        ) : null}
      </div>
    </div>
  );
}

interface MemoryConfigFieldsProps {
  paths: { memory: string };
  memoryMin: number;
  memoryMax: number;
  memoryMarkCount?: number;
  readonly?: boolean;
  /** 容量单位，直建默认 Gi；计价跟随选型 quantityUnit */
  quantityUnit?: string;
}

export function MemoryConfigFields({
  paths,
  memoryMin,
  memoryMax,
  memoryMarkCount,
  readonly,
  quantityUnit = DEFAULT_QUANTITY_UNITS.memory,
}: MemoryConfigFieldsProps) {
  const { t } = useTranslation('cluster');

  const checkMemoryLimit = (value: number | undefined, cb: (error?: string) => void) => {
    if (value == null) {
      cb(t('required', { content: t('mem') }));
      return;
    }
    const gi = Number(value);
    if (!Number.isFinite(gi) || gi < memoryMin) {
      cb(`内存最小值为 ${memoryMin} ${quantityUnit}`);
      return;
    }
    if (memoryMax > 0 && gi > memoryMax) {
      cb(`${t('limitlarge')}${memoryMax}`);
      return;
    }
    cb();
  };

  return (
    <CapacitySliderRow className={styles.memoryRow} unit={quantityUnit}>
      <FormItem
        initialValue={1}
        label={t('mem')}
        field={paths.memory}
        rules={readonly ? undefined : [{ required: true, validator: checkMemoryLimit }]}
      >
        <CapacitySlider
          min={memoryMin}
          max={memoryMax}
          markCount={memoryMarkCount ?? LARGE_RANGE_SLIDER_MARK_COUNT}
          translateUnit={false}
          unit={quantityUnit}
          disabled={readonly}
          readOnly={readonly}
        />
      </FormItem>
    </CapacitySliderRow>
  );
}

/** GPU 弹窗内额外元数据隐藏域（容器驱动、资源名等） */
export function GpuExtraPersistFields({ gpuPath }: { gpuPath: string }) {
  return (
    <>
      <FormItem field={`${gpuPath}.name`} hidden>
        <Input />
      </FormItem>
      <FormItem field={`${gpuPath}.card.name`} hidden>
        <Input />
      </FormItem>
      <FormItem field={`${gpuPath}.card.request`} hidden>
        <Input />
      </FormItem>
      <FormItem field={`${gpuPath}.memory`} hidden>
        <Input />
      </FormItem>
      <FormItem field={`${gpuPath}.driverVersion`} hidden>
        <Input />
      </FormItem>
      <FormItem field={`${gpuPath}.cudaVersion`} hidden>
        <Input />
      </FormItem>
      <FormItem field={`${gpuPath}.code`} hidden>
        <Input />
      </FormItem>
    </>
  );
}
